import SubdirectoryArrowRight from "@mui/icons-material/SubdirectoryArrowRight";
import { Box, Stack, Tab, Tabs, Typography } from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router";
import { programHref } from "./program-switcher";

import type { ProgramNode } from "../program-tree";
import type { FC, ReactNode } from "react";

const opens = (node: ProgramNode): boolean => node.children.length > 0;

/**
 * The folder that ?tab= chooses: a folder one level down that the caller can open, and that has no
 * folders of its own. With no such folder, the tabs show "All".
 */
export const selectedFolder = (current: ProgramNode, tab: string | null): ProgramNode | null =>
  current.children.find((node) => node.id === tab && node.accessible && !opens(node)) ?? null;

/**
 * A tab's title, and the number of Communications in it when the counts are known, on one line. A
 * Tab with no icon stacks its children, so they are in one span.
 */
const Label: FC<{ title: string; count: number | undefined }> = ({ title, count }) => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "baseline" }}>
    {title}
    {count === undefined ? null : " "}
    {count === undefined ? null : (
      <Box component="span" sx={{ ml: 0.75, typography: "caption", color: "text.secondary" }}>
        {count}
      </Box>
    )}
  </Box>
);

/**
 * The folders one level down of a program: "All" and each folder, with the number of Communications
 * in each, folders below it included. A folder with its own folders opens a level down, and any
 * other one is chosen with ?tab=. A folder the caller cannot open is shown, with no count, but cannot
 * be chosen. `action`, such as the button that adds a folder, goes at the end.
 */
export const SubProgramTabs: FC<{
  current: ProgramNode;
  counts?: ReadonlyMap<string, number>;
  action?: ReactNode;
}> = ({ current, counts, action }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const programs = current.children;

  if (programs.length === 0) {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", pb: 2 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          No folders here yet.
        </Typography>
        {action}
      </Stack>
    );
  }

  const selected = selectedFolder(current, searchParams.get("tab"))?.id ?? "all";
  const count = (node: ProgramNode): number | undefined =>
    counts && node.accessible ? (counts.get(node.id) ?? 0) : undefined;

  const tabs = (
    <Tabs
      value={selected}
      onChange={(_, value: string) => {
        if (value === "all") setSearchParams({});
        else if (programs.some((node) => node.id === value && !opens(node)))
          setSearchParams({ tab: value });
      }}
      variant="scrollable"
      scrollButtons="auto"
      aria-label="Folders"
      // The theme's gap between tabs is only between tabs with the same styles, so not next to
      // a tab with an icon.
      sx={{
        flex: 1,
        minWidth: 0,
        "& .MuiTab-root + .MuiTab-root": { ml: 3 },
        "& .MuiTab-root:not(.Mui-selected, .Mui-disabled)": { color: "text.primary" },
      }}
    >
      <Tab value="all" label={<Label title="All" count={count(current)} />} />
      {programs.map((node) =>
        opens(node) ? (
          <Tab
            key={node.id}
            value={node.id}
            label={<Label title={node.title} count={count(node)} />}
            disabled={!node.accessible}
            component={RouterLink}
            to={programHref(node.id)}
            icon={<SubdirectoryArrowRight fontSize="small" />}
            iconPosition="end"
            sx={{ minHeight: 48, py: 1.5 }}
          />
        ) : (
          <Tab
            key={node.id}
            value={node.id}
            label={<Label title={node.title} count={count(node)} />}
            disabled={!node.accessible}
          />
        ),
      )}
    </Tabs>
  );

  return action ? (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      {tabs}
      {action}
    </Stack>
  ) : (
    tabs
  );
};
